<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  authenticatedUser: { type: Boolean, required: true },
  isFilledList: { type: Boolean, default: false },
  marketId: { type: String, required: false },
  showAllMarkets: { type: Boolean, required: true },
  isPositions: { type: Boolean, default: false },
});

const { t } = useI18n({ useScope: 'global' });

const infoMessage = computed(() => {
  if (!props.authenticatedUser) {
    const loginToSee = t('modules.myOrders.helpers.loginToSee');

    return loginToSee;
  }
  const [base, quote] = props.marketId?.split('_') ?? [];
  const isMessageForAllMarkets = props.showAllMarkets && base && quote;

  if (props.isPositions) {
    const noPositions = t('modules.myOrders.helpers.noOpenPositions');
    const noPositionsForMarket = t('modules.myOrders.helpers.noOpenPositionsForMarket', { market: `${base}/${quote}` });

    return isMessageForAllMarkets ? noPositions : noPositionsForMarket;
  }

  if (props.isFilledList) {
    const noFilledOrders = t('modules.myOrders.helpers.noFills');
    const noFilledOrdersForMarket = t('modules.myOrders.helpers.noFillsForMarket', { market: `${base}/${quote}` });

    return isMessageForAllMarkets ? noFilledOrders : noFilledOrdersForMarket;
  }

  const noOpenOrders = t('modules.myOrders.helpers.noOpenOrders');
  const noOpenOrdersForMarket = t('modules.myOrders.helpers.noOpenOrdersForMarket', { market: `${base}/${quote}` });

  return isMessageForAllMarkets ? noOpenOrders : noOpenOrdersForMarket;
});

const seeFullHistoryRouteLink = computed(() => ({
  name: 'trading-accounts.orders',
  query: {
    status: 'inactive',
  },
}));
</script>

<template>
  <div class="info-message-block">
    <ot-icon
      class="info-message-block__icon"
      :name="authenticatedUser ? 'layer' : 'lock-circle'"
      :inherit-stroke="true"
    />
    <span>
      {{ infoMessage }}
    </span>
    <template v-if="isFilledList && authenticatedUser">
      <i18n-t
        keypath="modules.myOrders.helpers.seeFullHistory.text"
        tag="div"
        scope="global"
      >
        <template #linkText>
          <ot-link-internal
            :to="seeFullHistoryRouteLink"
            :title="$t('modules.myOrders.helpers.seeFullHistory.linkText')"
          />
        </template>
      </i18n-t>
    </template>
  </div>
</template>

<style lang="scss">
.info-message-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 34px 0;
  color: rgb(var(--v-theme-text-secondary));
  font-size: var(--font-size-xs);

  .xs & {
    padding-right: var(--padding-x-xs);
    padding-left: var(--padding-x-xs);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
  }

  &__icon {
    width: 36px;
    margin-bottom: 5px;
    color: rgb(var(--v-theme-text-secondary));
  }
}
</style>
